import React, { useEffect } from 'react';
import Lenis from 'lenis';
import './App.css';

import Header from './components/Header';
import HeroVideo from './components/HeroVideo';
import SatelliteToFields from './components/SatelliteToFields';
import Milestones from './components/Milestones';
import GalleryParallax from './components/GalleryParallax';
import LiveDashboard from './components/LiveDashboard';
import FeatureTabs from './components/FeatureTabs';
import StackedPanels from './components/StackedPanels';
import FaqSection from './components/FaqSection';
import About from './components/About';
import PhoneStack from './components/PhoneStack';
import { gsap, ScrollTrigger, prefersReduced } from './hooks/useScrollStory';

export default function App() {
  /* Lenis yumuşak kaydırmayı sürer, ScrollTrigger onunla aynı saatte çalışır.
     İki ayrı döngü olursa animasyonlar kaydırmanın gerisinde kalır. */
  useEffect(() => {
    if (prefersReduced()) return undefined;

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
      anchors: true, // #demo gibi bağlantılar Lenis ile kayar, yoksa geri çekilir
    });

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    ScrollTrigger.config({ ignoreMobileResize: true });

    /* Geliştirme sırasında sahneleri dışarıdan sürebilmek için. */
    if (process.env.NODE_ENV !== 'production') window.__lenis = lenis;

    /* İçerik geç geldikçe (ağır videolar, lazy görseller, açılan SSS)
       ölçüleri tazelemek gerekiyor. AMA refresh KAYDIRMA SIRASINDA
       çalışırsa sahne sıçrayıp takılıyor. O yüzden kaydırma bitip
       durduğunda tazeliyoruz; kaydırırken bekletiyoruz. */
    let scrolling = false;
    let idleTimer = null;
    let wantRefresh = false;
    const doRefresh = () => { lenis.resize(); ScrollTrigger.refresh(); };
    const scheduleRefresh = () => {
      if (scrolling) { wantRefresh = true; return; }
      doRefresh();
    };
    lenis.on('scroll', () => {
      ScrollTrigger.update();
      scrolling = true;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        scrolling = false;
        if (wantRefresh) { wantRefresh = false; doRefresh(); }
      }, 240);
    });

    const raf = requestAnimationFrame(scheduleRefresh);
    const timers = [500, 1500, 3000].map((ms) => setTimeout(scheduleRefresh, ms));
    Array.from(document.images).forEach((img) => {
      if (!img.complete) img.addEventListener('load', scheduleRefresh, { once: true });
    });
    const onLoad = () => scheduleRefresh();
    window.addEventListener('load', onLoad);

    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      if (idleTimer) clearTimeout(idleTimer);
      window.removeEventListener('load', onLoad);
    };
  }, []);

  /* sekme gizliyken döngüler dursun */
  useEffect(() => {
    const onVis = () => document.body.classList.toggle('paused', document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[200] focus:px-5 focus:py-2.5 focus:rounded-lg focus:bg-lime focus:text-black focus:font-mono focus:text-[13px]"
      >
        İçeriğe geç
      </a>

      <Header />

      <main id="main" tabIndex={-1}>
        {/* 1) Hero: kaydırmayla sarılan video, el ve fidan → kök */}
        <HeroVideo />
        {/* 2) Uygulama ekranları hemen üstüne biniyor */}
        <PhoneStack />
        {/* 3+4+5) Uydu, tarla katmanları, çiftçiye giriş ve onun
            gözünden hastalık analizi TEK sahnede. Aradaki hiçbir
            adımda alttan kayarak gelen bir bölüm yok. */}
        <SatelliteToFields />
        {/* Ortada: sahadaki rakamlar (odometre sayaç) */}
        <Milestones />
        {/* Hemen altında: parallax görsel duvarı (kaydırınca sıralar kayar) */}
        <GalleryParallax />
        {/* Görsellerin altında: canlı animasyonlu uygulama paneli */}
        <LiveDashboard />
        {/* Sekmeli özellikler: sekmeye basınca görsel+metin değişiyor */}
        <FeatureTabs />
        {/* Gör → Anla → Takip et: 3B tıklanabilir kartlar */}
        <StackedPanels />
        {/* SSS: açılır-kapanır sorular */}
        <FaqSection />
        {/* Kapanış: CTA + Hakkımızda + İletişim */}
        <About />
      </main>
    </>
  );
}
